/**
 * O contrato de autenticacao pelo IDP, do lado do navegador.
 *
 * Existe uma interface, e nao chamada direta ao Keycloak, pela mesma razao
 * operacional do hostzone: o app precisa rodar em dev e em CI sem o IDP de pe.
 * Quem cumpre esse papel e o `createMockIdpProvider`, e ele so serve porque
 * passa pelo mesmo `sessionFromClaims` do provider real — e o que garante que
 * os dois entreguem a mesma `IdpSession`.
 */

/**
 * Taxonomia do IDP (ADR 002/003 do `seazone-idp`). Role e derivada das relacoes
 * de perfil do Sapron, nunca de `main_role`.
 */
export const IDP_ROLES = [
  'Owner',
  'Partner',
  'Host',
  'CoHost',
  'Attendant',
  'CleaningAgent',
  'Investor',
  'ConstructionCompany',
  'Seazone',
  'Admin',
] as const;

export type IdpRole = (typeof IDP_ROLES)[number];

export interface IdpSession {
  /** `sub` do IDP: estavel, mas nao e a identidade de negocio. */
  readonly sub: string;
  /**
   * Claim `sapron_user_id`, o vinculo com o `account_user` da base legada.
   *
   * Numero, nao texto: o mapper do realm declara `jsonType.label: "long"`. E
   * referencia de vinculo, nunca chave de identidade — a chave e o e-mail
   * (ADR 001 do IDP).
   */
  readonly sapronUserId?: number;
  readonly name: string;
  readonly email: string;
  readonly roles: readonly IdpRole[];
  /** Flat, no formato `modulo:acao`. */
  readonly permissions: readonly string[];
  /** Expiracao do token, em segundos desde a epoca (claim `exp`). */
  readonly expiresAt: number;
}

/**
 * O que o app guarda de um login — **so em memoria**.
 *
 * Nem localStorage, nem sessionStorage, nem cookie legivel: o contrato proibe
 * token fora da memoria do processo
 * (`seazone-idp/docs/integracao/proibicoes.md`, item 1). O preco e conhecido e
 * aceito: recarregar a pagina joga isso fora, e quem devolve a sessao e o
 * re-login silencioso (`prompt=none`) contra a sessao SSO do IDP.
 */
export interface IdpTokens {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly idToken?: string;
  /** Expiracao do access token, em segundos desde a epoca. */
  readonly expiresAt: number;
}

export interface AuthorizeOptions {
  readonly state: string;
  readonly nonce: string;
  readonly codeChallenge: string;
  readonly redirectUri: string;
  /**
   * `prompt=none` — pede ao IDP que resolva pela sessao SSO ou devolva
   * `login_required`, sem nunca mostrar tela. E o que restaura a sessao depois
   * de um F5 sem pedir credencial de novo.
   */
  readonly silent?: boolean;
}

export interface ExchangeOptions {
  readonly code: string;
  readonly codeVerifier: string;
  readonly nonce: string;
  readonly redirectUri: string;
}

export interface IdpProvider {
  /**
   * Assincrono porque o provider real tira o `authorization_endpoint` do
   * discovery, que e chamada de rede.
   */
  authorizeUrl(options: AuthorizeOptions): Promise<string>;
  exchangeCode(options: ExchangeOptions): Promise<IdpTokens>;
  refresh(refreshToken: string): Promise<IdpTokens>;
  /**
   * `nonce` so e exigido na volta do callback: e la que ele prova que este
   * `id_token` responde a esta tentativa de login. No refresh o Keycloak
   * reemite o token com o `nonce` da sessao original, e conferir de novo
   * derrubaria uma sessao valida.
   */
  toSession(tokens: IdpTokens, options?: { readonly nonce?: string }): Promise<IdpSession>;
  /**
   * Logout RP-initiated. Devolve `undefined` quando o provider nao tem sessao
   * central para encerrar, que e o caso do duble — ai o logout do app e so
   * esvaziar a memoria.
   */
  endSession?(options: {
    readonly idToken?: string;
    readonly postLogoutRedirectUri: string;
  }): Promise<{ readonly url: string; readonly params: Record<string, string> } | undefined>;
}

/** Erro de autenticacao que o app sabe traduzir para uma tela legivel. */
export class IdpAuthError extends Error {
  readonly name = 'IdpAuthError';

  /** Codigo curto e estavel para o log estruturado — nunca a mensagem inteira. */
  readonly code: string;

  /**
   * A causa original, guardada a mao: o `lib` do projeto e ES2020, onde
   * `new Error(msg, { cause })` ainda nao existe.
   */
  readonly causa?: unknown;

  constructor(code: string, message: string, options?: { cause?: unknown }) {
    super(message);
    this.code = code;
    this.causa = options?.cause;
  }
}
